import React, { useState } from 'react';
import { Check, Copy, Pencil, RefreshCw, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { Tooltip } from '../../components/ui/Tooltip';

const ActionButton = ({ label, onClick, active, danger, children, disabled }) => (
  <Tooltip content={label} position="top">
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        'inline-flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition',
        'hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-40 dark:hover:bg-neutral-800 dark:hover:text-neutral-200',
        active && 'bg-neutral-100 text-neutral-950 dark:bg-neutral-800 dark:text-white',
        danger && active && 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400'
      )}
    >
      {children}
    </button>
  </Tooltip>
);

export const AssistantMessageActions = ({
  feedback,
  onCopy,
  onRegenerate,
  onRevert,
  onLike,
  onDislike,
  showCopy = true,
  showRegenerate = true,
  disabled = false,
  className = ''
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await onCopy?.();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={classNames('mt-1.5 flex items-center gap-0.5', className)}>
      {showCopy ? (
        <ActionButton label={copied ? t('chatCopied') : t('chatCopy')} onClick={handleCopy} disabled={disabled}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </ActionButton>
      ) : null}
      {showRegenerate ? (
        <ActionButton label={t('chatRegenerate')} onClick={onRegenerate} disabled={disabled}>
          <RefreshCw size={14} />
        </ActionButton>
      ) : null}
      {onRevert ? (
        <ActionButton label={t('revertMessageTooltip')} onClick={onRevert} disabled={disabled}>
          <RotateCcw size={14} />
        </ActionButton>
      ) : null}
      <ActionButton
        label={t('chatLike')}
        onClick={onLike}
        active={feedback === 'like'}
        disabled={disabled}
      >
        <ThumbsUp size={14} fill={feedback === 'like' ? 'currentColor' : 'none'} />
      </ActionButton>
      <ActionButton
        label={t('chatDislike')}
        onClick={onDislike}
        active={feedback === 'dislike'}
        danger
        disabled={disabled}
      >
        <ThumbsDown size={14} fill={feedback === 'dislike' ? 'currentColor' : 'none'} />
      </ActionButton>
    </div>
  );
};

export const UserMessageActions = ({
  onCopy,
  onEdit,
  disabled = false,
  className = ''
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await onCopy?.();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={classNames('mt-1.5 flex items-center gap-0.5', className)}>
      <ActionButton label={t('chatEdit')} onClick={onEdit} disabled={disabled}>
        <Pencil size={14} />
      </ActionButton>
      <ActionButton label={t('chatCopy')} onClick={handleCopy} disabled={disabled}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </ActionButton>
    </div>
  );
};

export const copyTextToClipboard = async (text) => {
  const value = String(text || '');
  if (!value) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const area = document.createElement('textarea');
  area.value = value;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};
